import { Trans } from '@lingui/react'

import {
  useAcceptSingleInstallmentsOfferMutation,
  useSelectCashAmountMutation,
} from '@twisto/api-graphql'
import { TextButton } from '@twisto/components/atoms/button'
import { Container } from '@twisto/components/atoms/container'
import {
  FormattedCurrencyNumber,
  FormattedNumber,
} from '@twisto/components/atoms/format'
import { ModalSplitLayout, useIsInModal } from '@twisto/components/atoms/modal'
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@twisto/components/atoms/table'
import { Typography } from '@twisto/components/atoms/typography'

import { useFlow } from '../../flow/flow.context'

import * as styles from './single-installments-offer-step.styles'

export const SingleInstallmentsOfferStep = () => {
  const { sessionId, flow } = useFlow<'SingleInstallmentsOfferStep'>()
  const isCalledInModal = useIsInModal()

  const {
    amountPerPeriod,
    annualPercentageRate,
    extraAmount,
    periodsCount,
    principalAmount,
    totalAmount,
  } = flow.currentStep.offer

  const [acceptSingleInstallmentsOffer, { loading }] =
    useAcceptSingleInstallmentsOfferMutation()

  const termValues = [
    {
      title: <Trans id="flow.SingleInstallmentsOfferStep.orderValue" />,
      value: <FormattedCurrencyNumber value={principalAmount} />,
    },
    {
      title: <Trans id="flow.SingleInstallmentsOfferStep.extra" />,
      value: <FormattedCurrencyNumber value={extraAmount} />,
    },
    {
      title: <Trans id="flow.SingleInstallmentsOfferStep.apr" />,
      value: (
        <FormattedNumber
          maximumFractionDigits={2}
          minimumFractionDigits={2}
          style="percent"
          value={annualPercentageRate}
        />
      ),
    },
    {
      title: <Trans id="flow.SingleInstallmentsOfferStep.total" />,
      value: <FormattedCurrencyNumber value={totalAmount} />,
    },
  ]

  const handleContinue = () => {
    acceptSingleInstallmentsOffer({
      variables: {
        auth: {
          sessionId,
        },
      },
    })
  }

  const endContent = (
    <Container css={styles.buttonContainer} size="tiny">
      <TextButton fullWidth onClick={handleContinue} disabled={loading}>
        <Trans id="flow.SingleInstallmentsOfferStep.button" />
      </TextButton>
    </Container>
  )
  const startContent = (
    <Container css={styles.termsContainer} size="tiny">
      <header css={styles.header}>
        <Typography
          align="center"
          color="textTertiary"
          css={styles.title}
          fontWeight="medium"
          textTransform="uppercase"
          variant="b3"
        >
          <Trans id="flow.SingleInstallmentsOfferStep.title" />
        </Typography>
        <Typography
          align="center"
          color="textPrimary"
          fontWeight="heavy"
          variant="h3"
        >
          {periodsCount}
          <span css={styles.multiplication}> × </span>
          <FormattedCurrencyNumber value={amountPerPeriod} />
        </Typography>
      </header>
      <Table>
        <TableBody>
          {termValues.map(({ title, value }, index) => (
            <TableRow key={index}>
              <TableCell scope="row">
                <Typography color="textPrimary" variant="b3">
                  {title}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography color="textSecondary" variant="b3">
                  {value}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Container>
  )

  if (isCalledInModal) {
    return <ModalSplitLayout end={endContent} start={startContent} />
  }

  return (
    <>
      {startContent}
      {endContent}
    </>
  )
}
